import ApiClient from '@config/network';
import {
  Checkout,
  FormValues,
  PaymentMethod,
  PAYMENT_METHOD_TYPE,
  STRIPE_PAYMENT_TYPE,
} from '@types';
import { isCheckoutConfig, isCheckoutParams } from '@types.guard';
import { createContext, useContext, useMemo } from 'react';
import { useQuery } from 'react-query';

export interface CheckoutConfigState {
  checkout: Checkout;
  checkoutData: Partial<FormValues>;
  status: 'idle' | 'loading' | 'error' | 'success';
  error: unknown;
}

const CheckoutConfigContext = createContext<CheckoutConfigState>({} as CheckoutConfigState);

const QUERY_KEY = 'widget.config';

export const CheckoutConfigProvider = ({
  children,
}: {
  children: Array<JSX.Element> | JSX.Element;
}): JSX.Element => {
  const params = useMemo(() => {
    return Object.fromEntries(new URLSearchParams(location.search));
  }, [window.location.search]);

  if (!Object.keys(params).length) {
    throw new Error('Missing checkout UUID');
  }

  if (!isCheckoutParams(params)) {
    throw new Error('Invalid parameters');
  }

  const { data, ...req } = useQuery(
    [QUERY_KEY],
    () => ApiClient.get<Checkout>(`/rest/v1/checkout/${params?.co}`),
    { retry: false },
  );

  const checkoutData: Partial<FormValues> = useMemo(() => {
    let _cd: Partial<FormValues> = {
      legalPerson: data?.data?.legal_person || undefined,
      amount: data?.data?.amount || undefined,
      frequency: data?.data?.frequency || undefined,
      businessName: data?.data?.business_name || undefined,
      vat: data?.data?.vat || undefined,
      firstName: data?.data?.first_name || undefined,
      lastName: data?.data?.last_name || undefined,
      ssn: data?.data?.ssn || undefined,
      email: data?.data?.email || undefined,
      phone: data?.data?.phone || undefined,
      mobile: data?.data?.mobile || undefined,
      address: data?.data?.address || undefined,
      city: data?.data?.city || undefined,
      locality: data?.data?.locality || undefined,
      postalCode: data?.data?.postal_code || undefined,
      country: data?.data?.country || undefined,
      acceptedPrivacyFields: data?.data.privacy_fields || [],
    };

    // Remove null and undefined values from initValues
    _cd = Object.fromEntries(
      Object.entries(_cd).filter(([_, value]) => value !== null && value !== undefined),
    ) as Partial<FormValues>;

    return _cd;
  }, [data]);

  if (data) {
    if (!isCheckoutConfig(data.data)) {
      console.error('Form configuration malformed', data.data);
      throw new Error('Form configuration malformed');
    }

    // if (data.data?.enabled_payment_methods?.length === 0) {
    //   throw new Error('Form configuration error: No payment methods enabled');
    // }
  }
  if (data?.data.enabled_payment_methods) {
    data.data.enabled_payment_methods = data.data.enabled_payment_methods.filter(
      (pm) =>
        pm.type != PAYMENT_METHOD_TYPE.STRIPE || pm.stripe_payment_type != STRIPE_PAYMENT_TYPE.POS,
    );
    data.data.enabled_payment_methods.sort(comparePaymentMethods);
  }

  return (
    <CheckoutConfigContext.Provider
      value={{ checkout: data?.data as Checkout, ...req, checkoutData }}
    >
      {children}
    </CheckoutConfigContext.Provider>
  );
};

function comparePaymentMethods(a: PaymentMethod, b: PaymentMethod) {
  if (a.type === PAYMENT_METHOD_TYPE.STRIPE && b.type !== PAYMENT_METHOD_TYPE.STRIPE) {
    return -1;
  } else if (a.type === PAYMENT_METHOD_TYPE.STRIPE) {
    if (a.stripe_payment_type === STRIPE_PAYMENT_TYPE.CREDIT_CARD) {
      return -1;
    }
  } else if (a.type === PAYMENT_METHOD_TYPE.PAYPAL && b.type !== PAYMENT_METHOD_TYPE.STRIPE) {
    return -1;
  } else if (a.type === PAYMENT_METHOD_TYPE.SATISPAY && b.type !== PAYMENT_METHOD_TYPE.STRIPE) {
    return -1;
  }

  return 0;
}

export const useCheckoutConfigContext = () => useContext(CheckoutConfigContext);

export default CheckoutConfigContext;
